import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FiEye, FiHeart, FiLink, FiMessageCircle, FiShare2, FiUpload } from 'react-icons/fi';

import PageHeader from '../Components/PageHeader/PageHeader';
import Breadcrumb from '../Components/Breadcrumb/Breadcrumb';
import Button from '../Components/Button/Button';
import EmptyState from '../Components/EmptyState/EmptyState';
import PlatformLoginCard from '../Components/PlatformLoginCard/PlatformLoginCard';
import LinkPlatformModal from '../Components/LinkPlatformModal/LinkPlatformModal';
import UploadVideoModal from '../Components/UploadVideoModal/UploadVideoModal';
import ConfirmDialog from '../Components/ConfirmDialog/ConfirmDialog';
import RefreshButton from '../Components/RefreshButton/RefreshButton';
import Banner from '../Components/Banner/Banner';
import StatBlock from '../Components/StatBlock/StatBlock';
import ContributionBreakdown from '../Components/ContributionBreakdown/ContributionBreakdown';
import PlatformBadge, { getPlatform } from '../Components/PlatformBadge/PlatformBadge';

import {
  getAccount,
  getAccountGroup,
  getAccountStats,
  listLogins,
  linkPlatformLogin,
  unlinkLogin,
  startInstagramLink,
  startTikTokLink,
  startYouTubeLink,
  startXLink,
  startFacebookLink,
} from '../Utilities/api';
import {
  getUploadReadiness,
  getBlobUploadUrl,
  putBlob,
  createUploadJob,
  startUploadScope,
} from '../Utilities/uploadApi';

import './accountDetail.css';

/**
 * OAuth callback query-param conventions agreed with the BE.
 *   <prefix>_linked=1    -> success toast
 *   <prefix>_error=<msg> -> error toast
 */
const OAUTH_CALLBACK_PARAMS = {
  instagram: { success: 'ig_linked', error: 'ig_error' },
  tiktok: { success: 'tt_linked', error: 'tt_error' },
  youtube: { success: 'yt_linked', error: 'yt_error' },
  x: { success: 'x_linked', error: 'x_error' },
  facebook: { success: 'fb_linked', error: 'fb_error' },
};

const OAUTH_STARTERS = {
  instagram: startInstagramLink,
  tiktok: startTikTokLink,
  youtube: startYouTubeLink,
  x: startXLink,
  facebook: startFacebookLink,
};

function AccountDetail() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [group, setGroup] = useState(null);
  const [logins, setLogins] = useState([]);
  const [stats, setStats] = useState(null);
  const [showLink, setShowLink] = useState(false);
  const [pendingUnlink, setPendingUnlink] = useState(null);
  const [banner, setBanner] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [readiness, setReadiness] = useState(null);

  const load = useCallback(async () => {
    const [a, ls, st] = await Promise.all([
      getAccount(accountId),
      listLogins(accountId),
      getAccountStats(accountId),
    ]);
    setAccount(a);
    setLogins(ls);
    setStats(st);
    if (a) {
      const g = await getAccountGroup(a.accountGroupId);
      setGroup(g);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  // Pick up redirect params from any platform's OAuth callback.
  useEffect(() => {
    let matched = false;
    const next = new URLSearchParams(searchParams);

    for (const [platformId, keys] of Object.entries(OAUTH_CALLBACK_PARAMS)) {
      const label = getPlatform(platformId)?.label || platformId;
      if (searchParams.get(keys.success)) {
        setBanner({ type: 'success', message: `${label} linked successfully.` });
        load();
        next.delete(keys.success);
        matched = true;
      } else if (searchParams.get(keys.error)) {
        const reason = searchParams.get(keys.error).replace(/_/g, ' ');
        setBanner({ type: 'error', message: `${label} linking failed: ${reason}.` });
        next.delete(keys.error);
        matched = true;
      }
    }

    if (matched) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLink = async ({ platform, platformUsername }) => {
    await linkPlatformLogin({ accountId, platform, platformUsername });
    await load();
  };

  const handleOAuthStart = async (platform) => {
    const starter = OAUTH_STARTERS[platform];
    if (!starter) return;
    const label = getPlatform(platform)?.label || platform;
    const res = await starter(accountId);
    if (res?.url) {
      window.location.href = res.url;
    } else {
      setBanner({ type: 'error', message: `Could not start ${label} linking.` });
    }
  };

  const handleConfirmUnlink = async () => {
    if (!pendingUnlink) return;
    await unlinkLogin(pendingUnlink.id);
    setPendingUnlink(null);
    await load();
  };

  const openUpload = async () => {
    const r = await getUploadReadiness(accountId);
    setReadiness(r);
    setShowUpload(true);
  };

  const handleEnableUploads = async (login) => {
    const res = await startUploadScope(login.platform, accountId);
    if (res?.authorize_url) window.location.href = res.authorize_url;
  };

  const handleReconnect = async (login) => {
    const starter = OAUTH_STARTERS[login.platform];
    if (starter) {
      const res = await starter(accountId);
      if (res?.url) window.location.href = res.url;
    }
  };

  const handleUploadSubmit = async ({
    videoFile,
    thumbFile,
    durationSec,
    captionMode,
    commonCaption,
    targets,
  }) => {
    // 1. Get signed blob URL for the video.
    const blob = await getBlobUploadUrl({
      accountId,
      filename: videoFile.name,
      contentType: videoFile.type,
      sizeBytes: videoFile.size,
    });

    // 2. PUT the file (mocked).
    await putBlob(blob.blob_url, videoFile);

    // 3. Thumbnail: get separate blob URL and upload if provided.
    let thumbBlobPath = null;
    if (thumbFile) {
      const thumbBlob = await getBlobUploadUrl({
        accountId,
        filename: thumbFile.name,
        contentType: thumbFile.type,
        sizeBytes: thumbFile.size,
      });
      await putBlob(thumbBlob.blob_url, thumbFile);
      thumbBlobPath = thumbBlob.blob_path;
    }

    // 4. Create the job.
    const idempotencyKey =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;

    const job = await createUploadJob({
      account_id: accountId,
      idempotency_key: idempotencyKey,
      source_blob_path: blob.blob_path,
      duration_sec: durationSec,
      thumbnail_blob_path: thumbBlobPath,
      caption_mode: captionMode,
      common_caption: commonCaption,
      targets,
    });

    setShowUpload(false);
    navigate(`/upload-jobs/${job.job_id}`);
  };

  const breakdownRows = useMemo(() => {
    if (!stats?.logins) return [];
    return [...stats.logins]
      .sort((a, b) => (b.stats?.views || 0) - (a.stats?.views || 0))
      .map((l) => {
        const label = getPlatform(l.platform)?.label || l.platform;
        return {
          key: l.loginId,
          identity: (
            <>
              <PlatformBadge platform={l.platform} size="sm" />
              <span>
                {label} — {l.platformUsername}
              </span>
            </>
          ),
          meta: `${l.uploadCount} ${l.uploadCount === 1 ? 'upload' : 'uploads'}`,
          stats: l.stats,
        };
      });
  }, [stats]);

  if (!account) return null;

  const existingPlatforms = logins.map((l) => l.platform);
  const totalStats = stats?.totals?.stats;
  const totalUploads = stats?.totals?.uploadCount ?? 0;

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: group?.name || '…', to: group ? `/groups/${group.id}` : undefined },
          { label: account.name },
        ]}
      />

      {banner && (
        <Banner
          type={banner.type}
          message={banner.message}
          onDismiss={() => setBanner(null)}
        />
      )}

      <PageHeader
        title={account.name}
        subtitle={account.description || 'Linked platforms for this account'}
        meta={
          <span>
            {logins.length} {logins.length === 1 ? 'platform linked' : 'platforms linked'}
          </span>
        }
        actions={
          <>
            <RefreshButton
              level="account"
              id={account.id}
              onRefreshed={load}
              label="Refresh all"
            />
            {logins.length > 0 && (
              <Button icon={<FiUpload />} onClick={openUpload}>
                Upload video
              </Button>
            )}
            <Button icon={<FiLink />} onClick={() => setShowLink(true)}>
              Link platform
            </Button>
          </>
        }
      />

      {logins.length === 0 ? (
        <EmptyState
          icon={<FiShare2 />}
          title="No platforms linked"
          description="Link Instagram, TikTok, or YouTube to start pulling uploads and stats for this account."
          action={
            <Button icon={<FiLink />} onClick={() => setShowLink(true)}>
              Link your first platform
            </Button>
          }
        />
      ) : (
        <>
          {totalStats && (
            <div className="account-detail-stats">
              <StatBlock label="Total views" value={totalStats.views} icon={<FiEye />} />
              <StatBlock label="Total likes" value={totalStats.likes} icon={<FiHeart />} />
              <StatBlock
                label="Total comments"
                value={totalStats.comments}
                icon={<FiMessageCircle />}
              />
              <StatBlock label="Total shares" value={totalStats.shares} icon={<FiShare2 />} />
            </div>
          )}

          {totalStats && totalUploads > 0 && (
            <ContributionBreakdown
              title="Contribution by platform"
              totals={totalStats}
              rows={breakdownRows}
              onRowClick={(row) => navigate(`/logins/${row.key}`)}
              emptyMessage="No uploads pulled on any linked platform yet."
            />
          )}

          <h2 className="account-detail-section-title">Linked platforms</h2>

          <div className="account-detail-list">
            {logins.map((l) => (
              <PlatformLoginCard
                key={l.id}
                login={l}
                uploadCount={l.uploadCount ?? 0}
                onUnlink={(lg) => setPendingUnlink(lg)}
                onRefreshed={load}
              />
            ))}
          </div>
        </>
      )}

      <LinkPlatformModal
        open={showLink}
        onClose={() => setShowLink(false)}
        onSubmit={handleLink}
        onOAuthStart={handleOAuthStart}
        existingPlatforms={existingPlatforms}
      />

      <UploadVideoModal
        open={showUpload}
        accountId={accountId}
        readiness={readiness}
        onClose={() => setShowUpload(false)}
        onSubmit={handleUploadSubmit}
        onEnableUploads={handleEnableUploads}
        onReconnect={handleReconnect}
        onLinkPlatform={() => {
          setShowUpload(false);
          setShowLink(true);
        }}
      />

      <ConfirmDialog
        open={!!pendingUnlink}
        title="Unlink platform?"
        message={`This will remove the ${pendingUnlink?.platform} link and delete every upload we've pulled from it. You can re-link it later.`}
        confirmLabel="Unlink"
        danger
        onClose={() => setPendingUnlink(null)}
        onConfirm={handleConfirmUnlink}
      />
    </>
  );
}

export default AccountDetail;
